import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, Users, Mail, Phone, ChevronRight, Building2 } from "lucide-react";
import { toast } from "sonner";
import EmployeeFormDialog from "@/components/EmployeeFormDialog";
import EmployeeAvatar from "@/components/EmployeeAvatar";
import { initials } from "@/lib/utils-app";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { Pagination } from "@/components/Pagination";
import { usePagination } from "@/hooks/usePagination";

export default function EmployeesListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [branchFilter, setBranchFilter] = useState("__all__");
  const [branches, setBranches] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [prefillTalentId, setPrefillTalentId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { pageItems, page, setPage, totalPages } = usePagination(employees, 20);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (isAdmin && branchFilter && branchFilter !== "__all__") params.branch_id = branchFilter;
      const { data } = await api.get("/employees", { params });
      setEmployees(data);
    } catch (e) {
      toast.error("Falha ao carregar colaboradores");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      api.get("/branches").then(({ data }) => setBranches(data)).catch(() => {});
    }
  }, [isAdmin]);

  useEffect(() => {
    const t = setTimeout(() => fetchEmployees(), 250);
    return () => clearTimeout(t);
     
  }, [search, branchFilter]);

  useEffect(() => {
    const tid = searchParams.get("prefill_talent_id");
    if (!tid) return;
    (async () => {
      try {
        const { data } = await api.get(`/talents/${tid}`);
        setPrefill({ name: data.name || "", phone: data.phone || "", notes: data.notes || "", status: "ativo" });
        setPrefillTalentId(tid);
        setDialogOpen(true);
      } catch {
        toast.error("Talento não encontrado");
      } finally {
        // limpa o query param para não reabrir se o usuário fechar o dialog
        const p = new URLSearchParams(searchParams);
        p.delete("prefill_talent_id");
        setSearchParams(p, { replace: true });
      }
    })();
    // eslint-disable-next-line
  }, []);

  const handleCreate = async (payload) => {
    const { data: created } = await api.post("/employees", payload);
    setDialogOpen(false);
    // Se veio de um talento, apaga o talento após contratação
    if (prefillTalentId) {
      try { await api.delete(`/talents/${prefillTalentId}`); } catch {}
      setPrefillTalentId(null);
      setPrefill(null);
    }
    setTimeout(() => {
      toast.success(prefillTalentId ? "Candidato contratado e removido do banco de talentos" : "Colaborador cadastrado");
      fetchEmployees();
    }, 300);
    return created;
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-sm text-slate-500 mb-1">Equipe</p>
          <h1 className="text-3xl md:text-4xl font-outfit font-semibold tracking-tight">Colaboradores</h1>
          <p className="text-sm text-slate-500 mt-2">Gerencie todos os colaboradores e seus documentos.</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          data-testid="add-employee-button"
          className="bg-brand-900 hover:bg-brand-800 text-white h-10"
        >
          <Plus className="w-4 h-4 mr-2" />
          Adicionar Colaborador
        </Button>
      </header>

      <Card className="border-slate-200 shadow-none overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar por nome, CPF, cargo ou departamento..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="employees-search-input"
              className="pl-9 h-10"
            />
          </div>
          {isAdmin && (
            <Select value={branchFilter} onValueChange={setBranchFilter}>
              <SelectTrigger className="w-[200px] h-10" data-testid="branch-filter-select">
                <Building2 className="w-4 h-4 mr-1 text-slate-500" />
                <SelectValue placeholder="Filial" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todas as filiais</SelectItem>
                {branches.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
              </SelectContent>
            </Select>
          )}
          <div className="text-xs text-slate-500" data-testid="employees-count">
            {employees.length} {employees.length === 1 ? "colaborador" : "colaboradores"}
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Carregando...</div>
        ) : employees.length === 0 ? (
          <div className="p-16 text-center" data-testid="empty-employees-state">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
              <Users className="w-5 h-5 text-slate-500" />
            </div>
            <h3 className="font-outfit font-semibold text-base">Nenhum colaborador cadastrado</h3>
            <p className="text-sm text-slate-500 mt-1 mb-4">Adicione seu primeiro colaborador para começar.</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-brand-900 hover:bg-brand-800 text-white">
              <Plus className="w-4 h-4 mr-2" /> Adicionar Colaborador
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Nome</TableHead>
                <TableHead className="hidden md:table-cell">Cargo / Depto</TableHead>
                {isAdmin && <TableHead className="hidden lg:table-cell">Filial</TableHead>}
                <TableHead className="hidden xl:table-cell">Contato</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageItems.map((emp) => (
                <TableRow
                  key={emp.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => navigate(`/colaboradores/${emp.id}`)}
                  data-testid={`employee-row-${emp.id}`}
                >
                  <TableCell className="py-4">
                    <div className="flex items-center gap-3">
                      <EmployeeAvatar employee={emp} size={36} />
                      <div>
                        <div className="font-medium text-sm">{emp.name}</div>
                        <div className="text-xs text-slate-500">{emp.cpf || "—"}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <div className="text-sm">{emp.role || "—"}</div>
                    <div className="text-xs text-slate-500">{emp.department || "—"}</div>
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="hidden lg:table-cell">
                      <div className="text-xs inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-50 text-brand-800 border border-brand-100">
                        <Building2 className="w-3 h-3" /> {emp.branch_name || "—"}
                      </div>
                    </TableCell>
                  )}
                  <TableCell className="hidden xl:table-cell">
                    <div className="text-xs space-y-0.5">
                      {emp.email && <div className="flex items-center gap-1.5 text-slate-600"><Mail className="w-3 h-3" />{emp.email}</div>}
                      {emp.phone && <div className="flex items-center gap-1.5 text-slate-600"><Phone className="w-3 h-3" />{emp.phone}</div>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold border ${
                        emp.status === "ativo"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-slate-100 text-slate-600 border-slate-200"
                      }`}
                    >
                      {emp.status === "ativo" ? "Ativo" : "Inativo"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Pagination page={page} totalPages={totalPages} onChange={setPage} testId="employees-pagination" />
      </Card>

      <EmployeeFormDialog
        open={dialogOpen}
        onOpenChange={(v) => { setDialogOpen(v); if (!v) { setPrefill(null); setPrefillTalentId(null); } }}
        onSubmit={handleCreate}
        initial={prefill}
        title={prefillTalentId ? "Contratar Candidato" : "Novo Colaborador"}
      />
    </div>
  );
}
